import { appConfig } from './config';

let initialization: Promise<void> | undefined;

const clampRate = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(1, parsed)) : fallback;
};

const normalizeEnvironment = (value?: string) => {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'production' || normalized === 'prod') return 'production';
  if (normalized === 'test' || normalized === 'testing' || normalized === 'pre' || normalized === 'staging') return 'test';
  if (window.location.hostname === 'admin.test.guanzhaihome.com') return 'test';
  if (window.location.hostname === 'admin.guanzhaihome.com') return 'production';
  return 'development';
};

const sensitiveKey = /authorization|cookie|password|passwd|token|secret|api[-_]?key|private[-_]?key|phone|mobile/i;
const urlKey = /^(url|referrer|file)$/i;

const sanitizeValue = (value: unknown, key = '', seen = new WeakSet<object>()): unknown => {
  if (sensitiveKey.test(key)) return '[REDACTED]';
  if (typeof value === 'string') {
    if (urlKey.test(key)) {
      try {
        const url = new URL(value, window.location.origin);
        url.search = '';
        url.hash = '';
        return url.toString();
      } catch {
        return value.split(/[?#]/, 1)[0];
      }
    }
    return value.replace(
      /(authorization|password|token|secret|api[-_]?key|phone|mobile)\s*[:=]\s*[^\s,;]+/gi,
      '$1=[REDACTED]',
    );
  }
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return '[CIRCULAR]';
  seen.add(value);
  if (Array.isArray(value)) return value.map(item => sanitizeValue(item, key, seen));
  return Object.fromEntries(Object.entries(value).map(([childKey, childValue]) => [
    childKey,
    childKey === 'snapshots' || childKey === 'records_data' || childKey === 'image'
      ? undefined
      : sanitizeValue(childValue, childKey, seen),
  ]));
};

export const initializeAdminMonitoring = () => {
  initialization ??= (async () => {
    const dsn = process.env.REACT_APP_SENTRY_DSN?.trim();
    if (!dsn || process.env.REACT_APP_SENTRY_ENABLED === 'false') return;

    const Sentry = await import('@sentry/react');
    const tracedApiOrigin = appConfig.apiBaseUrl || window.location.origin;
    Sentry.init({
      dsn,
      environment: normalizeEnvironment(process.env.REACT_APP_SENTRY_ENVIRONMENT),
      release: process.env.REACT_APP_RELEASE ?? 'guanzhai-admin@1.0.0',
      sendDefaultPii: false,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: clampRate(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0.05),
      tracePropagationTargets: [tracedApiOrigin],
      beforeBreadcrumb: breadcrumb => {
        if (breadcrumb.category === 'console' || breadcrumb.category === 'ui.click') return null;
        return sanitizeValue(breadcrumb) as typeof breadcrumb;
      },
      beforeSend: event => sanitizeValue(event) as typeof event,
      beforeSendTransaction: event => sanitizeValue(event) as typeof event,
    });
    Sentry.setTag('app.component', 'admin');
  })().catch(error => {
    if (process.env.NODE_ENV !== 'production') console.warn('Sentry Admin initialization failed', error);
  });
  return initialization;
};
